import type { ReactElement } from 'react';
import type { BackupReportDto, RestoreReportDto } from '@gr/core/platform/operations/backups';
import { TaskKind, TaskStatus, type TaskDto } from '@gr/core/platform/tasks/dto';
import { useT } from '../../../../app/di';
import { formatFileSize, formatNumber } from '../../../../shared/format';

export interface BackupTaskSummaryProps {
  task: TaskDto;
}

/** OPS-UC-8, OPS-UC-11, TASK-UC-3: итог задачи копии или восстановления. */
export function BackupTaskSummary({ task }: BackupTaskSummaryProps): ReactElement | null {
  const { t, tp, locale } = useT();
  const number = (value: number): string => formatNumber(value, locale);
  if (!task.report) return null;
  const lines: string[] = [];
  if (task.kind === TaskKind.Backup) {
    const report = task.report as BackupReportDto;
    if (report.demo) lines.push(t('operations.backups.report.demo'));
    if (report.size !== null) lines.push(t('operations.backups.report.archive', { name: report.name, size: formatFileSize(report.size, locale) }));
    if (report.files > 0) lines.push(t('operations.backups.report.archived', { files: number(report.files), bytes: formatFileSize(report.bytes, locale) }));
    if (report.missing > 0) lines.push(tp('operations.backups.report.missing', report.missing, { n: report.missing }));
  } else if (task.kind === TaskKind.Restore) {
    const report = task.report as RestoreReportDto;
    lines.push(t('operations.backups.report.source', { name: report.name, version: report.version }));
    if (report.migrated > 0) lines.push(tp('operations.backups.report.migrated', report.migrated, { n: report.migrated }));
    lines.push(t('operations.backups.report.restored', { restored: number(report.restored), unchanged: number(report.unchanged), failed: number(report.failed) }));
    if (report.removed > 0) lines.push(t('operations.backups.report.removed', { n: number(report.removed) }));
    if (task.status === TaskStatus.Completed || task.status === TaskStatus.CompletedWithErrors) lines.push(t('operations.backups.report.relogin'));
  }
  if (lines.length === 0) return null;
  return (
    <ul className="u-list-reset u-stack u-gap-1 u-small">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}
